import styled from 'styled-components';

import { colors } from '../../styles/colors';
import { colors as remoteControlColors } from '../../styles/remoteControlColors';

export const Base = styled.button`
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  cursor: pointer;
  font-size: 1em;
  font-family: 'Open Sans', sans-serif;
  font-weight: bold;
  outline: none;
  padding: 12px;
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.2s ease-in-out;
  width: 100%;
`;

export const Primary = styled(Base)`
  color: ${colors.White};
  transition: all 0.3s ease-in-out;
  border-radius: 10px;
  background-color: ${colors.Blue300};
  border-color: ${colors.Blue300};

  :hover {
    background-color: ${colors.BlueMenu};
    border-color: ${colors.BlueMenu};
  }
`;

export const Green = styled(Base)`
  color: #01FF9B;
  transition: all 0.3s ease-in-out;
  border-radius: 10px;
  background-color: ${colors.Blue300};
  border-color: ${colors.Blue300};

  :hover {
    background-color: ${colors.BlueMenu};
    border-color: ${colors.BlueMenu};
  }
`;

export const Secondary = styled(Base)`
  background-color: ${colors.White};
  border-color: ${colors.Pink200};
  color: ${colors.Pink200};
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${colors.White};
    color: ${colors.Pink300};
    border-color: ${colors.Pink300};
  }
`;

export const BlueWhite = styled(Base)`
  color: #363BC4;
  transition: all 0.3s ease-in-out;
  border-radius: 5px;
  background-color: ${colors.White};
  border-color: #363BC4;

  :hover {
    background-color: #363BC4;
    border-color: #363BC4;
    color: ${colors.White};
    svg {
      path {
        stroke: white;
      }
    }
  }
`;

export const Disabled = styled(Base)`
  border-radius: 10px;
  background-color: ${colors.Grey100};
  border-color: ${colors.Grey100};
  color: ${colors.White};
  cursor: not-allowed;
`;
export const RedInvert = styled(Base)`
  background-color: ${colors.White};
  border-color: ${colors.White};
  color: ${colors.Red};
  border-radius: 5px;
  &:hover {
    background-color: ${colors.LightRed};
  }
`;

export const Blue = styled(Base)`
  background-color: ${colors.Blue300};
  border-color: ${colors.Blue300};
  color: ${colors.White};
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${colors.Blue400};
    border-color: ${colors.Blue400};
  }
`;

export const BlueInvert = styled(Base)`
  background-color: ${colors.White};
  border-color: ${colors.White};
  color: ${colors.DarkBlue};
  border-radius: 5px;
  &:hover {
    background-color: ${colors.VeryLightBlue};
  }
`;

export const BorderBlue = styled(Base)`
  background-color: ${colors.White};
  border-color: ${colors.Blue300};
  border-width: 2px;
  color: ${colors.Blue300};
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${colors.White};
    border-color: ${colors.Blue300};
  }
`;

export const Red = styled(Base)`
  color: ${colors.White};
  transition: all 0.3s ease-in-out;
  border-radius: 10px;
  background-color: ${colors.RedDark};
  border-color: ${colors.RedDark};

  :hover {
    background-color: ${colors.Red};
    border-color: ${colors.Red};
  }
`;

export const RedInvertWithBorder = styled(Base)`
  background-color: ${colors.White};
  border-color: ${colors.Red};
  color: ${colors.Red};
  border-radius: 10px;
  font-weight: 600;
  &:hover {
    background-color: ${colors.LightRed};
  }
`;

export const RcGrey = styled(Base)`
  border: none;
  border-radius: 12px;
  background-color: ${remoteControlColors.Grey200};
  color: ${remoteControlColors.Grey400};
  font-size: 14px;
  font-weight: 600;
  text-transform: capitalize;
`;

export const RcBlue = styled(Base)`
  border: none;
  border-radius: 8px;
  background: ${remoteControlColors.Blue700};
  color: ${remoteControlColors.White};
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  text-transform: capitalize;

  &:disabled {
    background: ${remoteControlColors.Grey200};
    color: ${remoteControlColors.Grey400};
  }
`;

export const RcLinkBlue = styled(Base)`
  border: none;
  border-radius: 8px;
  background: ${remoteControlColors.White};
  color: ${remoteControlColors.Blue700};
  font-size: 18px;
  font-weight: 600;
  line-height: 150%;
  text-transform: capitalize;

  &:disabled {
    background: ${remoteControlColors.Grey100};
    color: ${remoteControlColors.Grey300};
  }
`;

export const NeutralStrokePrimary = styled(Base)`
  border: 1px solid ${colors.stroke['soft-200']};
  border-radius: 4px;
  background: ${colors.bg['white-0']};
  color: ${colors.text['main-900']};
  font-size: 11px;
  font-weight: 600;
  line-height: 150%;
  text-transform: none;
  padding: 4px 12px;
  transition: background 0.1s ease-in-out, color 0.1s ease-in-out;

  & > svg {
    stroke: ${colors.icon['primary-base']};
  }

  &:hover {
    border-color: transparent;
    background: ${colors.brand.primary.light};
  }

  &:active {
    background: ${colors.brand.primary.lighter};
  }

  &:disabled, &:disabled:hover {
    background: ${colors.bg['weak-100']};
    color: ${colors.text['disabled-300']};
    border-color: transparent;

    & > svg {
      stroke: ${colors.icon['disabled-300']};
    }
  }
`;

const Filled = styled(Base)`
  border: 0;
  border-radius: 4px;
  color: ${colors.text['white-0']};
  font-size: 11px;
  font-weight: 600;
  line-height: 150%;
  text-transform: none;
  padding: 4px 12px;
  transition: background 0.1s ease-in-out, color 0.1s ease-in-out;

  & > svg {
    stroke: ${colors.icon['white-0']};
  }

  &:disabled, &:disabled:hover {
    background: ${colors.bg['weak-100']};
    color: ${colors.text['disabled-300']};

    & > svg {
      stroke: ${colors.icon['disabled-300']};
    }
  }
`;

export const InfoFilled = styled(Filled)`
  background: ${colors.state.info.base};

  &:hover {
    background: ${colors.state.info.dark};
  }

  &:active {
    background: ${colors.state.info.base};
  }
`;

export const DangerFilled = styled(Filled)`
  background: ${colors.state.danger.base};

  &:hover {
    background: ${colors.state.danger.dark};
  }

  &:active {
    background: ${colors.state.danger.base};
  }
`;

export const PrimaryFilled = styled(Filled)`
  background: ${colors.brand.primary.base};

  &:hover {
    background: ${colors.brand.primary.dark};
  }

  &:active {
    background: ${colors.brand.primary.base};
  }
`;

export const Ghost = styled(Base)`
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: ${colors.text['white-0']};
  font-size: 11px;
  font-weight: 600;
  line-height: 150%;
  text-transform: none;
  padding: 4px 12px;
  transition: background 0.1s ease-in-out, color 0.1s ease-in-out;

  & > svg {
    stroke: ${colors.icon['white-0']};
  }

  &:disabled, &:disabled:hover {
    background: ${colors.bg['weak-100']};
    color: ${colors.text['disabled-300']};

    & > svg {
      stroke: ${colors.icon['disabled-300']};
    }
  }
`;

export const NeutralGhost = styled(Ghost)`
  color: ${colors.text['sub-500']};

  &:hover {
    background: ${colors.bg['weak-100']};
    color: ${colors.text['main-900']};
  }

  &:active {
    background: ${colors.brand.primary.base};
    color: ${colors.text['sub-500']};
    border: 1px solid ${colors.stroke['soft-200']};
  }
`;

export const PrimaryGhost = styled(Ghost)`
  color: ${colors.text['primary-base']};

  & > svg {
    stroke: ${colors.icon['primary-base']};
  }

  &:hover {
    background: ${colors.brand.primary.light};
    color: ${colors.text['primary-base']};
  }

  &:active {
    background: ${colors.brand.primary.lighter};
    color: ${colors.text['primary-base']};
    border: 1px solid ${colors.stroke['primary-base']};
  }
`;

export const Stroke = styled(Base)`
  border: 1px solid;
  border-radius: 4px;
  padding: 4px 12px;
  transition: background 0.1s ease-in-out, color 0.1s ease-in-out;
  background-color: ${colors.bg['white-0']};
  font-size: 11px;
  font-weight: 600;
  line-height: 150%;
  text-transform: none;

  &:disabled, &:disabled:hover {
    background: ${colors.bg['weak-100']};
    color: ${colors.text['disabled-300']};

    & > svg {
      stroke: ${colors.icon['disabled-300']};
    }
  }
`;

export const PrimaryStroke = styled(Stroke)`
  color: ${colors.text['primary-base']};
  border-color: ${colors.stroke['primary-base']};

  &:hover {
    background: ${colors.brand.primary.light};
    border-color: ${colors.brand.primary.light};
  }

  &:active {
    background: ${colors.brand.primary.lighter};
    border-color: ${colors.stroke['primary-base']};
    color: ${colors.text['primary-base']};
  }

  &:disabled, &:disabled:hover {
    border-color: ${colors.bg['weak-100']};
  }
`;
